'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { contentApi } from '@/lib/api';
import { useRouter } from 'next/navigation';
import {
  Sparkles, Upload, X, Calendar, Send, Image as ImageIcon,
  FileText, Video, Loader2, Check, ArrowLeft, ArrowRight, Wand2,
} from 'lucide-react';
import { GlowButton } from '@/components/ui/glow-button';
import { NeonCard } from '@/components/ui/neon-card';
import { useContent } from '@/contexts/ContentContext';
import { BRollSection } from '@/components/content/BRollSection';

type ContentType = 'image' | 'video' | 'reel';

const PLATFORMS = [
  { id: 'linkedin', label: 'LinkedIn', emoji: '💼' },
  { id: 'instagram', label: 'Instagram', emoji: '📸' },
  { id: 'youtube', label: 'YouTube', emoji: '🎬' },
  { id: 'tiktok', label: 'TikTok', emoji: '🎵' },
  { id: 'x', label: 'X / Twitter', emoji: '𝕏' },
  { id: 'facebook', label: 'Facebook', emoji: '👤' },
];

const STEPS = ['Type & Platform', 'Content', 'Media', 'Schedule & Submit'];

interface WizardFields {
  topic: string;
  hook: string;
  script: string;
  caption: string;
  videoPrompt: string;
  audioDirection: string;
}

interface CreationWizardProps {
  editPostId?: string;
}

export function CreationWizard({ editPostId }: CreationWizardProps) {
  const router = useRouter();
  const { selectedProjectId, selectedProjectSlug, projects, refreshPosts } = useContent();

  const [step, _setStep] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('content-wizard-step');
      return saved ? parseInt(saved, 10) : 0;
    }
    return 0;
  });
  const setStep = (s: number) => {
    _setStep(s);
    localStorage.setItem('content-wizard-step', String(s));
  };
  const [contentType, _setContentType] = useState<ContentType>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('cw-contentType');
      if (saved === 'video' || saved === 'image' || saved === 'reel') return saved;
    }
    return 'image';
  });
  const setContentType = (v: ContentType) => { _setContentType(v); localStorage.setItem('cw-contentType', v); };
  const [videoDuration, _setVideoDuration] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('cw-videoDuration');
      return saved || '30';
    }
    return '30';
  });
  const setVideoDuration = (v: string) => { _setVideoDuration(v); localStorage.setItem('cw-videoDuration', v); };
  const [customDuration, setCustomDuration] = useState('');
  const [audioDuration, setAudioDuration] = useState('15');
  const [audioMood, setAudioMood] = useState('');
  const [selectedPlatforms, _setSelectedPlatforms] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      try { const s = localStorage.getItem('cw-platforms'); if (s) { const p = JSON.parse(s); if (Array.isArray(p) && p.length) return p; } } catch {}
    }
    return ['linkedin'];
  });
  const setSelectedPlatforms = (v: string[] | ((prev: string[]) => string[])) => {
    _setSelectedPlatforms(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      localStorage.setItem('cw-platforms', JSON.stringify(next));
      return next;
    });
  };
  const [fields, _setFields] = useState<WizardFields>(() => {
    if (typeof window !== 'undefined') {
      try { const s = localStorage.getItem('cw-fields'); if (s) return JSON.parse(s); } catch {}
    }
    return { topic: '', hook: '', script: '', caption: '', videoPrompt: '', audioDirection: '' };
  });
  const setFields = (v: WizardFields | ((prev: WizardFields) => WizardFields)) => {
    _setFields(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      localStorage.setItem('cw-fields', JSON.stringify(next));
      return next;
    });
  };
  const [generating, setGenerating] = useState<string | null>(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [postId, setPostId] = useState<string | null>(editPostId || null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedVideoPath, setUploadedVideoPath] = useState('');
  const [uploadedImagePath, setUploadedImagePath] = useState('');
  const [uploadedAudioPath, _setUploadedAudioPath] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('cw-uploadedAudioPath') || '';
    return '';
  });
  const setUploadedAudioPath = (v: string) => { _setUploadedAudioPath(v); localStorage.setItem('cw-uploadedAudioPath', v); };
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [videoGenStarted, setVideoGenStarted] = useState(false);
  const [checkingVideo, setCheckingVideo] = useState(false);
  const [videoGenMessage, setVideoGenMessage] = useState('');
  const [audioGenMessage, _setAudioGenMessage] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('cw-audioGenMessage') || '';
    return '';
  });
  const setAudioGenMessage = (v: string) => { _setAudioGenMessage(v); localStorage.setItem('cw-audioGenMessage', v); };

  const [currentPreview, _setCurrentPreview] = useState<{path: string; planKey: string; prompt: string} | null>(() => {
    if (typeof window !== 'undefined') {
      try { const s = localStorage.getItem('cw-currentPreview'); return s ? JSON.parse(s) : null; } catch { return null; }
    }
    return null;
  });
  const setCurrentPreview = (v: {path: string; planKey: string; prompt: string} | null) => { _setCurrentPreview(v); localStorage.setItem('cw-currentPreview', v ? JSON.stringify(v) : ''); };

  const [extendDuration, setExtendDuration] = useState('30');
  const [topicIdeas, setTopicIdeas] = useState<string[]>([]);
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [voices, setVoices] = useState<{id: string; name: string; labels: Record<string,string>; preview_url?: string}[]>([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [playingPreview, setPlayingPreview] = useState<string | null>(null);
  const [generatingVoiceover, setGeneratingVoiceover] = useState(false);
  const [voiceoverOptions, _setVoiceoverOptions] = useState<string[]>(() => {
    if (typeof window !== 'undefined') { try { return JSON.parse(localStorage.getItem('cw-voiceoverOptions') || '[]'); } catch { return []; } }
    return [];
  });
  const setVoiceoverOptions = (v: string[]) => { _setVoiceoverOptions(v); localStorage.setItem('cw-voiceoverOptions', JSON.stringify(v)); };
  const [voiceoverMessage, setVoiceoverMessage] = useState('');
  const [voiceoverScript, _setVoiceoverScript] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('cw-voiceoverScript') || '';
    return '';
  });
  const setVoiceoverScript = (v: string) => {
    _setVoiceoverScript(v);
    localStorage.setItem('cw-voiceoverScript', v);
    if (postIdRef.current && v) {
      fetch(`/api/content/posts/${postIdRef.current}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceoverScript: v }),
      }).catch(() => {});
    }
  };
  // Track available Remotion compositions and user-selected target
  const [compositions, setCompositions] = useState<{id: string; title?: string; project?: string}[]>([]);
  const [compositionId, _setCompositionId] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('cw-compositionId') || '';
    return '';
  });
  const setCompositionId = (v: string) => { _setCompositionId(v); localStorage.setItem('cw-compositionId', v); };
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const videoInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Load available voices
  useEffect(() => {
    fetch('/api/content/generate-audio')
      .then(r => r.json())
      .then(data => {
        if (data.voices?.length) {
          setVoices(data.voices);
          if (!selectedVoice) setSelectedVoice(data.voices[0].id);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch compositions and auto-select if none saved
  useEffect(() => {
    fetch('/api/content/compositions')
      .then(r => r.json())
      .then(data => {
        const comps = data.compositions || [];
        setCompositions(comps);
        // If saved compositionId still exists, keep it
        const saved = localStorage.getItem('cw-compositionId');
        if (saved && comps.find((c: {id: string}) => c.id === saved)) return;
        // Auto-select by project slug or first available
        if (selectedProjectSlug) {
          const match = comps.find((c: {project?: string}) => c.project === selectedProjectSlug);
          if (match) { setCompositionId(match.id); return; }
        }
        if (comps.length > 0) setCompositionId(comps[0].id);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load existing post for editing
  useEffect(() => {
    if (!editPostId) return;
    fetch(`/api/content/posts/${editPostId}`)
      .then(r => r.json())
      .then(post => {
        if (post.error) return;
        setFields({
          topic: post.topic || '',
          hook: post.hook || '',
          script: post.script || '',
          caption: post.caption || '',
          videoPrompt: post.video_prompt || '',
          audioDirection: post.audio_direction || '',
        });
        if (post.platform) {
          setSelectedPlatforms(post.platform.split(',').map((s: string) => s.trim()));
        }
        if (post.content_type) setContentType(post.content_type as ContentType);
        if (post.video_path) setUploadedVideoPath(post.video_path);
        if (post.image_path) setUploadedImagePath(post.image_path);
        if (post.audio_path) setUploadedAudioPath(post.audio_path);
        if (post.scheduled_at) setScheduledAt(new Date(post.scheduled_at).toISOString().slice(0, 16));
      });
  }, [editPostId]);

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const postIdRef = useRef(postId);
  postIdRef.current = postId;

  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;

  // Load latest draft (or clear) when user deliberately switches project
  const prevProjectRef = useRef<string | null>(null);
  const hasHydrated = useRef(false);
  useEffect(() => {
    // Skip until we have a real project ID (context may hydrate async)
    if (!selectedProjectId) return;
    // First time we see a real project ID — load draft instead of resetting
    if (!hasHydrated.current) {
      hasHydrated.current = true;
      prevProjectRef.current = selectedProjectId;
      // Load latest draft on initial mount if localStorage is empty
      if (!fields.topic && !fields.hook && !fields.script) {
        fetch(`/api/content/posts?projectId=${selectedProjectId}&status=draft&limit=1`)
          .then(r => r.json())
          .then(posts => {
            if (!Array.isArray(posts) || posts.length === 0) return;
            const post = posts[0];
            setFields({
              topic: post.topic || '',
              hook: post.hook || '',
              script: post.script || '',
              caption: post.caption || '',
              videoPrompt: post.video_prompt || '',
              audioDirection: post.audio_direction || '',
            });
            setPostId(post.id);
            if (post.platform) setSelectedPlatforms(post.platform.split(',').map((s: string) => s.trim()));
            if (post.content_type) setContentType(post.content_type as ContentType);
            if (post.audio_path) setUploadedAudioPath(post.audio_path);
            if (post.voiceover_path) setVoiceoverOptions([post.voiceover_path]);
            if (post.voiceover_script) setVoiceoverScript(post.voiceover_script);
          })
          .catch(() => {});
      }
      return;
    }
    if (selectedProjectId === prevProjectRef.current) return;
    prevProjectRef.current = selectedProjectId;

    // Reset wizard state for new project
    const emptyFields: WizardFields = { topic: '', hook: '', script: '', caption: '', videoPrompt: '', audioDirection: '' };
    setFields(emptyFields);
    setSelectedPlatforms(['linkedin']);
    setPostId(null);
    setTopicIdeas([]);
    setUploadedVideoPath('');
    setUploadedImagePath('');
    setUploadedAudioPath('');
    setCurrentPreview(null);
    setVoiceoverScript('');
    setVoiceoverOptions([]);
    setAudioGenMessage('');
    setVideoGenMessage('');
    setStep(0);

    // Try to load latest draft for this project
    fetch(`/api/content/posts?projectId=${selectedProjectId}&status=draft&limit=1`)
      .then(r => r.json())
      .then(posts => {
        if (!Array.isArray(posts) || posts.length === 0) return;
        const post = posts[0];
        setFields({
          topic: post.topic || '',
          hook: post.hook || '',
          script: post.script || '',
          caption: post.caption || '',
          videoPrompt: post.video_prompt || '',
          audioDirection: post.audio_direction || '',
        });
        setPostId(post.id);
        if (post.platform) setSelectedPlatforms(post.platform.split(',').map((s: string) => s.trim()));
        if (post.content_type) setContentType(post.content_type as ContentType);
        if (post.video_path) setUploadedVideoPath(post.video_path);
        if (post.image_path) setUploadedImagePath(post.image_path);
        if (post.audio_path) setUploadedAudioPath(post.audio_path);
        if (post.voiceover_path) setVoiceoverOptions([post.voiceover_path]);
        if (post.voiceover_script) setVoiceoverScript(post.voiceover_script);
      })
      .catch(() => {});
  }, [selectedProjectId]);

  const selectedPlatformsRef = useRef(selectedPlatforms);
  selectedPlatformsRef.current = selectedPlatforms;

  const contentTypeRef = useRef(contentType);
  contentTypeRef.current = contentType;

  // Auto-save debounce
  const autoSave = useCallback(async (currentFields: WizardFields) => {
    const projId = selectedProjectIdRef.current;
    const platforms = selectedPlatformsRef.current;
    const currentPostId = postIdRef.current;
    if (!projId || !platforms.length) return;

    const body = {
      projectId: projId,
      platform: platforms[0],
      contentType: contentTypeRef.current,
      topic: currentFields.topic || null,
      hook: currentFields.hook || null,
      script: currentFields.script || null,
      caption: currentFields.caption || null,
      videoPrompt: currentFields.videoPrompt || null,
      audioDirection: currentFields.audioDirection || null,
      status: 'draft',
    };

    if (currentPostId) {
      await fetch(`/api/content/posts/${currentPostId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } else {
      const res = await fetch('/api/content/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.id) {
        setPostId(data.id);
        postIdRef.current = data.id;
      }
    }
  }, []);

  const updateField = (field: keyof WizardFields, value: string) => {
    const newFields = { ...fieldsRef.current, [field]: value };
    setFields(newFields);
    fieldsRef.current = newFields;

    // Debounced auto-save
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    const timer = setTimeout(() => autoSave(newFields), 2000);
    setAutoSaveTimer(timer);
  };

  const togglePlatform = (id: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  // AI Generation
  const generateField = async (field: keyof WizardFields) => {
    if (!selectedProjectId) return;
    setGenerating(field);
    try {
      const context: Record<string, string> = {};
      for (const [k, v] of Object.entries(fieldsRef.current)) {
        if (k !== field && v) context[k] = v;
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 95000);
      const res = await fetch('/api/content/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field,
          currentValue: fieldsRef.current[field] || '',
          context,
          projectId: selectedProjectId,
          platforms: selectedPlatforms,
          contentType,
          videoDuration: contentTypeRef.current !== 'image' ? (videoDuration === 'custom' && customDuration ? customDuration : videoDuration) : undefined,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      const data = await res.json();
      if (data.generated) {
        updateField(field, data.generated);
      } else if (data.error) {
        console.error(`[generate] error for ${field}:`, data.error);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error && err.name === 'AbortError'
        ? 'Generation timed out — the AI gateway may be busy. Try again in a moment.'
        : 'Generation failed. Please try again.';
      console.error(`[generate] ${field}:`, msg);
    } finally {
      setGenerating(null);
    }
  };

  const generateTopicIdeas = async () => {
    if (!selectedProjectId) return;
    setGeneratingTopics(true);
    setTopicIdeas([]);
    try {
      const res = await fetch('/api/content/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field: 'topics',
          currentValue: fieldsRef.current.topic || '',
          context: {},
          projectId: selectedProjectId,
          platforms: selectedPlatforms,
          contentType,
          videoDuration: contentTypeRef.current !== 'image' ? (videoDuration === 'custom' && customDuration ? customDuration : videoDuration) : undefined,
        }),
      });
      const data = await res.json();
      if (data.generated) {
        try {
          // Parse JSON array from response (handle markdown code fences)
          let raw = data.generated.trim();
          if (raw.startsWith('```')) {
            raw = raw.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '').trim();
          }
          const ideas = JSON.parse(raw);
          if (Array.isArray(ideas)) {
            setTopicIdeas(ideas.slice(0, 4));
          }
        } catch {
          // If parsing fails, split by newlines
          const lines = data.generated.split('\n').filter((l: string) => l.trim()).slice(0, 4);
          setTopicIdeas(lines.map((l: string) => l.replace(/^\d+[\.\)]\s*/, '').replace(/^["']|["']$/g, '').trim()));
        }
      }
    } finally {
      setGeneratingTopics(false);
    }
  };

  const generateEverything = async () => {
    if (!selectedProjectId) return;
    setGeneratingAll(true);
    // Skip topic if already filled (user picked from ideas or typed manually)
    const fieldOrder: (keyof WizardFields)[] = [];
    if (!fieldsRef.current.topic) fieldOrder.push('topic');
    fieldOrder.push('hook', 'script', 'caption');
    if (contentType !== 'image') {
      fieldOrder.push('videoPrompt', 'audioDirection');
    }
    for (const field of fieldOrder) {
      await generateField(field);
    }
    setGeneratingAll(false);
  };

  // File upload
  const uploadFile = async (file: File, type: 'video' | 'image') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const res = await fetch('/api/content/upload', { method: 'POST', body: formData });
    const data = await res.json();
    return data.path || '';
  };

  const handleFileSelect = async (file: File, type: 'video' | 'image') => {
    if (type === 'video') {
      setVideoFile(file);
      const path = await uploadFile(file, 'video');
      setUploadedVideoPath(path);
    } else {
      setImageFile(file);
      const path = await uploadFile(file, 'image');
      setUploadedImagePath(path);
    }
  };

  // Submit
  const handleSubmit = async (status: 'draft' | 'pending_review' | 'scheduled') => {
    if (!selectedProjectId) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        projectId: selectedProjectId,
        platform: selectedPlatforms[0] || 'linkedin',
        topic: fields.topic || null,
        hook: fields.hook || null,
        script: fields.script || null,
        caption: fields.caption || null,
        videoPrompt: fields.videoPrompt || null,
        audioDirection: fields.audioDirection || null,
        videoPath: uploadedVideoPath || null,
        imagePath: uploadedImagePath || null,
        audioPath: uploadedAudioPath || null,
        status,
      };
      if (status === 'scheduled' && scheduledAt) {
        body.scheduledAt = new Date(scheduledAt).toISOString();
      }

      if (postId) {
        await fetch(`/api/content/posts/${postId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        await fetch('/api/content/posts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      await refreshPosts();
      router.push('/content');
    } finally {
      setSaving(false);
    }
  };

  // Generate button component
  const GenerateBtn = ({ field }: { field: keyof WizardFields }) => {
    const isGen = generating === field;
    return (
      <button
        onClick={() => generateField(field)}
        disabled={isGen || generatingAll || !selectedProjectId}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 hover:shadow-[0_0_8px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        title={isGen ? 'Generating... (may take up to 90s if gateway is busy)' : fields[field] ? 'Improve with AI' : 'Generate with AI'}
      >
        {isGen ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        {isGen ? 'Generating...' : '✨'}
      </button>
    );
  };

  const inputClass = "w-full bg-white/5 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-cyan-500 focus:outline-none";
  const textareaClass = `${inputClass} resize-none`;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <button
              onClick={() => setStep(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                i === step
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                  : i < step
                  ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                  : 'bg-white/5 text-gray-500 border border-gray-700'
              }`}
            >
              {i < step ? <Check className="w-3 h-3" /> : <span className="w-4 text-center">{i + 1}</span>}
              {s}
            </button>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-gray-700" />}
          </div>
        ))}
      </div>

      {/* Generate Everything */}
      {step === 1 && (
        <GlowButton
          variant="cyan"
          size="sm"
          onClick={generateEverything}
          disabled={generatingAll || !selectedProjectId}
        >
          {generatingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {generatingAll ? 'Generating...' : '✨ Generate Everything'}
        </GlowButton>
      )}

      {/* Step 1: Type & Platform */}
      {step === 0 && (
        <NeonCard glow="cyan" className="p-6 space-y-5">
          <div>
            <label className="block text-sm text-gray-400 mb-3">Content Type</label>
            <div className="grid grid-cols-3 gap-4">
              {[
                { id: 'image' as const, label: 'Image Post', emoji: '📸', desc: 'Static image with caption' },
                { id: 'video' as const, label: 'Video Post', emoji: '🎬', desc: 'Video with audio & script' },
                { id: 'reel' as const, label: 'Reel', emoji: '📱', desc: 'Vertical 9:16 short video' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setContentType(t.id)}
                  className={`flex flex-col items-center gap-2 px-6 py-6 rounded-xl text-sm font-medium border transition-all ${
                    contentType === t.id
                      ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.2)]'
                      : 'bg-white/5 border-gray-700 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <span className="text-3xl">{t.emoji}</span>
                  <span className="font-semibold">{t.label}</span>
                  <span className="text-xs text-gray-500">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Video Duration Selector (only for video content) */}
          {contentType !== 'image' && (
            <div>
              <label className="block text-sm text-gray-400 mb-3">Video Duration</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {[
                  { value: '15', label: '15s', desc: 'Social teaser' },
                  { value: '30', label: '30s', desc: 'Standard short' },
                  { value: '60', label: '60s', desc: 'Extended short' },
                  { value: '90', label: '90s', desc: 'Medium form' },
                  { value: '120', label: '2min', desc: 'Long form' },
                  { value: 'custom', label: 'Custom', desc: 'Custom duration' },
                ].map(d => (
                  <button
                    key={d.value}
                    onClick={() => {
                      setVideoDuration(d.value);
                      if (d.value !== 'custom') setCustomDuration('');
                    }}
                    className={`flex flex-col items-center gap-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                      videoDuration === d.value
                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400 shadow-[0_0_10px_rgba(0,240,255,0.15)]'
                        : 'bg-white/5 border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <span className="font-semibold">{d.label}</span>
                    <span className="text-xs text-gray-500">{d.desc}</span>
                  </button>
                ))}
              </div>
              {videoDuration === 'custom' && (
                <input
                  type="number"
                  value={customDuration}
                  onChange={(e) => setCustomDuration(e.target.value)}
                  placeholder="Enter seconds (e.g., 45)"
                  className="w-full bg-white/5 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none mt-2"
                  min="5"
                  max="600"
                />
              )}
              <p className="text-xs text-gray-500 mt-2">
                {(() => {
                  const dur = videoDuration === 'custom' && customDuration ? parseInt(customDuration) : parseInt(videoDuration);
                  if (isNaN(dur)) return 'Select or enter a duration';
                  const wordCount = Math.round(dur * 2.5);
                  const sceneCount = dur <= 15 ? '2-3' : dur <= 30 ? '4-5' : dur <= 60 ? '6-8' : dur <= 90 ? '8-10' : '10-12';
                  return `~${wordCount} words for voiceover, ~${sceneCount} scenes`;
                })()}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-400 mb-3">Platforms</label>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map(p => (
                <button
                  key={p.id}
                  onClick={() => togglePlatform(p.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-all ${
                    selectedPlatforms.includes(p.id)
                      ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                      : 'bg-white/5 border-gray-700 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  <span>{p.emoji}</span>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <GlowButton variant="cyan" size="sm" onClick={() => setStep(1)} disabled={!selectedPlatforms.length}>
              Next <ArrowRight className="w-4 h-4" />
            </GlowButton>
          </div>
        </NeonCard>
      )}

      {/* Step 2: Content Fields */}
      {step === 1 && (
        <NeonCard glow="cyan" className="p-6 space-y-5">
          {/* Topic */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-gray-400">Topic / Idea</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={generateTopicIdeas}
                  disabled={generatingTopics || generatingAll || !selectedProjectId}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 hover:shadow-[0_0_8px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingTopics ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  ✨ Generate 4 Ideas
                </button>
                <GenerateBtn field="topic" />
              </div>
            </div>
            <input
              value={fields.topic}
              onChange={(e) => { updateField('topic', e.target.value); setTopicIdeas([]); }}
              placeholder="What's this content about?"
              className={`${inputClass} ${generating === 'topic' ? 'animate-pulse border-cyan-500/50' : ''}`}
            />

            {/* Topic Ideas Grid */}
            {topicIdeas.length > 0 && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {topicIdeas.map((idea, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      updateField('topic', idea);
                      setTopicIdeas([]);
                    }}
                    className={`text-left p-3 rounded-lg border text-sm transition-all hover:shadow-[0_0_12px_rgba(0,240,255,0.15)] ${
                      fields.topic === idea
                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                        : 'bg-white/5 border-gray-700 text-gray-300 hover:border-cyan-500/40 hover:bg-cyan-500/5'
                    }`}
                  >
                    <span className="text-cyan-500 font-medium mr-1.5">{i + 1}.</span>
                    {idea}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Hook */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-gray-400">Hook</label>
              <GenerateBtn field="hook" />
            </div>
            <input
              value={fields.hook}
              onChange={(e) => updateField('hook', e.target.value)}
              placeholder="The scroll-stopping first line..."
              className={`${inputClass} ${generating === 'hook' ? 'animate-pulse border-cyan-500/50' : ''}`}
            />
          </div>

          {/* Script / Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-gray-400">Script / Body</label>
              <GenerateBtn field="script" />
            </div>
            <textarea
              value={fields.script}
              onChange={(e) => updateField('script', e.target.value)}
              placeholder="Full script or post body..."
              rows={8}
              className={`${textareaClass} ${generating === 'script' ? 'animate-pulse border-cyan-500/50' : ''}`}
            />
          </div>

          {/* Caption */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-gray-400">Caption</label>
              <GenerateBtn field="caption" />
            </div>
            <textarea
              value={fields.caption}
              onChange={(e) => updateField('caption', e.target.value)}
              placeholder="Platform caption with hashtags..."
              rows={4}
              className={`${textareaClass} ${generating === 'caption' ? 'animate-pulse border-cyan-500/50' : ''}`}
            />
          </div>

          {/* Video-specific fields */}
          {contentType !== 'image' && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-400">Video Prompt</label>
                  <GenerateBtn field="videoPrompt" />
                </div>
                <textarea
                  value={fields.videoPrompt}
                  onChange={(e) => updateField('videoPrompt', e.target.value)}
                  placeholder="Describe the video visuals..."
                  rows={3}
                  className={`${textareaClass} ${generating === 'videoPrompt' ? 'animate-pulse border-cyan-500/50' : ''}`}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-400">Audio Direction</label>
                  <GenerateBtn field="audioDirection" />
                </div>
                <input
                  value={fields.audioDirection}
                  onChange={(e) => updateField('audioDirection', e.target.value)}
                  placeholder="Music mood, SFX notes..."
                  className={`${inputClass} ${generating === 'audioDirection' ? 'animate-pulse border-cyan-500/50' : ''}`}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-400">🎙️ Voiceover Script</label>
                  <button
                    onClick={() => setVoiceoverScript(fields.script || fields.caption || fields.hook || '')}
                    className="text-xs text-cyan-500 hover:text-cyan-400"
                  >
                    Copy from script ↗
                  </button>
                </div>
                <textarea
                  value={voiceoverScript}
                  onChange={(e) => setVoiceoverScript(e.target.value)}
                  placeholder="Text for the voiceover narrator. Can differ from the post script — shorter, punchier, or a different take."
                  rows={4}
                  className={textareaClass}
                />
              </div>
            </>
          )}

          <div className="flex justify-between pt-2">
            <GlowButton variant="ghost" size="sm" onClick={() => setStep(0)}>
              <ArrowLeft className="w-4 h-4" /> Back
            </GlowButton>
            <GlowButton variant="cyan" size="sm" onClick={() => setStep(2)}>
              Next <ArrowRight className="w-4 h-4" />
            </GlowButton>
          </div>
        </NeonCard>
      )}

      {/* Step 3: Media */}
      {step === 2 && (
        <NeonCard glow="cyan" className="p-6 space-y-5">
          {/* Video upload */}
          {contentType !== 'image' && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">Video</label>
              <div
                onClick={() => videoInputRef.current?.click()}
                className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:border-cyan-500/50 transition-colors"
              >
                {videoFile || uploadedVideoPath ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-sm text-cyan-400">{videoFile?.name || uploadedVideoPath}</span>
                    <button onClick={(e) => { e.stopPropagation(); setVideoFile(null); setUploadedVideoPath(''); }}>
                      <X className="w-4 h-4 text-gray-500 hover:text-red-400" />
                    </button>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Drop video file or click to browse</p>
                  </div>
                )}
              </div>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f, 'video');
                }}
              />
            </div>
          )}

          {/* Image upload */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Image</label>
            <div
              onClick={() => imageInputRef.current?.click()}
              className="border-2 border-dashed border-gray-700 rounded-lg p-4 text-center cursor-pointer hover:border-cyan-500/50 transition-colors"
            >
              {imageFile || uploadedImagePath ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-cyan-400">{imageFile?.name || uploadedImagePath}</span>
                  <button onClick={(e) => { e.stopPropagation(); setImageFile(null); setUploadedImagePath(''); }}>
                    <X className="w-4 h-4 text-gray-500 hover:text-red-400" />
                  </button>
                </div>
              ) : (
                <div className="text-gray-500">
                  <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                  <p className="text-sm">Drop image or click to browse</p>
                </div>
              )}
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileSelect(f, 'image');
              }}
            />
          </div>

          {/* Audio Parameters (for video) */}
          {contentType !== 'image' && (
            <div className="border-t border-gray-700 pt-4 space-y-4">
              <label className="block text-sm text-gray-400 font-medium">Audio Parameters</label>

              {/* Composition Target Selector */}
              {compositions.length > 0 && (
                <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
                  <label className="block text-xs text-cyan-400 font-medium mb-1.5">🎬 Push Audio / Voiceover to Composition</label>
                  <select
                    value={compositionId}
                    onChange={(e) => setCompositionId(e.target.value)}
                    className="w-full bg-white/5 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="">— Select target composition —</option>
                    {compositions.map(c => (
                      <option key={c.id} value={c.id}>{c.title || c.id}</option>
                    ))}
                  </select>
                  {!compositionId && (
                    <p className="text-xs text-yellow-400/70 mt-1">⚠️ Select a composition before pushing audio</p>
                  )}
                </div>
              )}
              <div className="flex gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Duration</label>
                  <select
                    value={audioDuration}
                    onChange={(e) => setAudioDuration(e.target.value)}
                    className="bg-white/5 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="10">10s</option>
                    <option value="15">15s</option>
                    <option value="30">30s</option>
                    <option value="60">60s</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-2">Mood</label>
                <div className="flex flex-wrap gap-2">
                  {['Energetic', 'Calm', 'Dramatic', 'Upbeat', 'Corporate'].map(mood => (
                    <button
                      key={mood}
                      onClick={() => setAudioMood(audioMood === mood ? '' : mood)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        audioMood === mood
                          ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                          : 'bg-white/5 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* B-Roll Section */}
          {contentType !== 'image' && selectedProjectId && (
            <BRollSection
              script={fields.script}
              videoPrompt={fields.videoPrompt}
              projectId={selectedProjectId}
              contentType={contentType}
              compositionId={compositionId}
              postId={postId}
            />
          )}

          {/* AI Generation */}
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <label className="block text-sm text-gray-400">AI Generation</label>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={async () => {
                  if (!fields.videoPrompt) {
                    alert('Please fill in the Video Prompt field first (Step 2).');
                    return;
                  }
                  // Ensure post is saved first
                  if (!postIdRef.current) {
                    await autoSave(fieldsRef.current);
                  }
                  const currentPostId = postIdRef.current;
                  if (!currentPostId) {
                    alert('Could not save post. Please try again.');
                    return;
                  }
                  setGeneratingVideo(true);
                  setVideoGenMessage('Starting video generation agent...');
                  try {
                    const res = await fetch('/api/content/generate-video', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        postId: currentPostId,
                        videoPrompt: fields.videoPrompt,
                        projectId: selectedProjectId,
                      }),
                    });
                    const data = await res.json();
                    if (data.error) {
                      setVideoGenMessage(`❌ ${data.error}`);
                      return;
                    }
                    setVideoGenStarted(true);
                    setVideoGenMessage('');
                  } catch {
                    setVideoGenMessage('❌ Failed to start video generation.');
                  } finally {
                    setGeneratingVideo(false);
                  }
                }}
                disabled={generatingVideo}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 hover:shadow-[0_0_8px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50"
                title="Uses Remotion to create motion graphics from your script and video prompt."
              >
                {generatingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : '🎬'} Generate Video with AI
              </button>
              <button
                onClick={async () => {
                  if (!fields.audioDirection) {
                    alert('Please fill in the Audio Direction field first (Step 2).');
                    return;
                  }
                  if (!postIdRef.current) {
                    await autoSave(fieldsRef.current);
                  }
                  const currentPostId = postIdRef.current;
                  setGeneratingAudio(true);
                  setAudioGenMessage('🎵 Generating 10s preview with ElevenLabs Music AI...');
                  setCurrentPreview(null);
                  try {
                    const controller = new AbortController();
                    const timeout = setTimeout(() => controller.abort(), 180000);
                    const res = await fetch('/api/content/generate-audio', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        postId: currentPostId,
                        audioDirection: fields.audioDirection,
                        projectId: selectedProjectId,
                        duration: audioDuration,
                        mood: audioMood,
                      }),
                      signal: controller.signal,
                    });
                    clearTimeout(timeout);
                    const data = await res.json();
                    if (data.error) {
                      setAudioGenMessage(`❌ ${data.error}`);
                      return;
                    }
                    if (data.preview && data.track) {
                      setCurrentPreview(data.track);
                      setAudioGenMessage('✅ 10s preview ready! Like it? Extend it. Don\'t? Hit Regenerate.');
                    } else if (data.paths?.length) {
                      setCurrentPreview(null);
                      setUploadedAudioPath(data.paths[0]);
                      setAudioGenMessage('✅ Full track generated!');
                    } else {
                      setAudioGenMessage('⚠️ Audio generation completed but no files were returned.');
                    }
                  } catch {
                    setAudioGenMessage('❌ Audio generation failed or timed out.');
                  } finally {
                    setGeneratingAudio(false);
                  }
                }}
                disabled={generatingAudio}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 hover:shadow-[0_0_8px_rgba(0,240,255,0.3)] transition-all disabled:opacity-50"
                title="Uses ElevenLabs to create background music or sound effects."
                data-generate-audio="true"
              >
                {generatingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : '🔊'} Generate Audio with AI
              </button>
            </div>

            {/* Status messages */}
            {videoGenMessage && (
              <p className="text-sm text-gray-300">{videoGenMessage}</p>
            )}

            {/* Interactive video generation card */}
            {videoGenStarted && !uploadedVideoPath && (
              <div className="mt-3 p-4 rounded-lg border border-cyan-500/30 bg-cyan-500/5 space-y-3">
                <div className="flex items-center gap-2 text-cyan-400 font-medium">
                  <span className="text-lg">🎬</span> Video Generation Active
                </div>
                <p className="text-sm text-gray-300">Forge is building your Remotion scenes. Preview and iterate in Remotion Studio, then guide Forge via Chat.</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="http://localhost:3100"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded text-xs font-medium bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 transition-all"
                  >
                    Open Remotion Studio ↗
                  </a>
                  <a
                    href="/chat"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 rounded text-xs font-medium bg-purple-500/10 border border-purple-500/30 text-purple-400 hover:bg-purple-500/20 transition-all"
                  >
                    Open Chat to Guide Forge ↗
                  </a>
                  <button
                    onClick={async () => {
                      if (!postIdRef.current) return;
                      setCheckingVideo(true);
                      try {
                        const res = await fetch(`/api/content/posts/${postIdRef.current}`);
                        const data = await res.json();
                        if (data.video_path) {
                          setUploadedVideoPath(data.video_path);
                          setVideoGenStarted(false);
                          setVideoGenMessage('✅ Video rendered!');
                        } else {
                          setVideoGenMessage('Still working... check Remotion Studio or Chat.');
                          setTimeout(() => setVideoGenMessage(''), 3000);
                        }
                      } catch {
                        setVideoGenMessage('Could not check status.');
                      } finally {
                        setCheckingVideo(false);
                      }
                    }}
                    disabled={checkingVideo}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-white/5 border border-gray-600 text-gray-300 hover:border-cyan-500/50 transition-all disabled:opacity-50"
                  >
                    {checkingVideo ? '⏳ Checking...' : '🔄 Check Video Status'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Once you&apos;re happy with the preview, tell Forge to render the final video.</p>
              </div>
            )}

            {audioGenMessage && (
              <p className="text-sm text-gray-300">{audioGenMessage}</p>
            )}

            {/* Video preview */}
            {uploadedVideoPath && uploadedVideoPath.includes('video-') && (
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">Generated Video</label>
                <video controls src={uploadedVideoPath} className="w-full max-w-md rounded-lg border border-gray-700" />
              </div>
            )}

            {/* Audio preview options */}
            {currentPreview ? (
              <div className="mt-3 space-y-3 p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5">
                <p className="text-xs text-gray-400 font-medium">🎵 10s Preview</p>
                <audio controls src={currentPreview.path} className="w-full h-10" />

                <div className="flex items-center gap-3 flex-wrap">
                  {/* Extend controls */}
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={3}
                      max={300}
                      value={extendDuration}
                      onChange={(e) => setExtendDuration(e.target.value)}
                      className="bg-white/5 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:border-cyan-500 focus:outline-none w-20 text-center"
                    />
                    <span className="text-xs text-gray-500">sec (3–300)</span>
                  </div>

                  <button
                    onClick={async () => {
                      setGeneratingAudio(true);
                      setAudioGenMessage(`🎵 Extending to ${extendDuration}s...`);
                      try {
                        const res = await fetch('/api/content/generate-audio', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            type: 'music',
                            audioDirection: fields.audioDirection,
                            duration: extendDuration,
                            mood: audioMood,
                            compositionPlanKey: currentPreview.planKey,
                            postId: postIdRef.current,
                          }),
                        });
                        const data = await res.json();
                        if (data.error) {
                          setAudioGenMessage(`❌ ${data.error}`);
                          return;
                        }
                        const fullPath = data.paths?.[0] || data.primaryPath;
                        if (fullPath) {
                          setCurrentPreview(null);
                          setUploadedAudioPath(fullPath);
                          setAudioGenMessage(`✅ Full ${extendDuration}s track generated!`);
                          try {
                            const studioRes = await fetch('/api/content/studio-audio', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ audioPath: fullPath, compositionId, projectSlug: selectedProjectSlug, audioType: 'music' }),
                            });
                            const studioData = await studioRes.json();
                            if (studioData.ok) {
                              setAudioGenMessage(`✅ Full track ready & pushed to Remotion (${studioData.compositionId})! backgroundAudio = "${studioData.remotionPath}"`);
                            }
                          } catch { /* non-critical */ }
                        }
                      } catch {
                        setAudioGenMessage('❌ Failed to extend track.');
                      } finally {
                        setGeneratingAudio(false);
                      }
                    }}
                    disabled={generatingAudio}
                    className="px-4 py-1.5 rounded-lg text-sm font-medium bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-all disabled:opacity-50"
                  >
                    {generatingAudio ? <Loader2 className="w-4 h-4 animate-spin inline" /> : '✅'} Extend to {extendDuration}s
                  </button>

                  <button
                    onClick={async () => {
                      setGeneratingAudio(true);
                      setAudioGenMessage(`🔁 Looping to ${extendDuration}s (exact same audio)...`);
                      try {
                        const res = await fetch('/api/content/audio/loop', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ audioPath: currentPreview.path, targetSeconds: parseInt(extendDuration) }),
                        });
                        const data = await res.json();
                        if (data.error) { setAudioGenMessage(`❌ ${data.error}`); return; }
                        setCurrentPreview(null);
                        setUploadedAudioPath(data.path);
                        setAudioGenMessage(`✅ Looped to ${extendDuration}s! Free, identical audio.`);
                        try {
                          const sr = await fetch('/api/content/studio-audio', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ audioPath: data.path, compositionId, projectSlug: selectedProjectSlug, audioType: 'music' }) });
                          const sd = await sr.json();
                          if (sd.ok) setAudioGenMessage(`✅ Looped to ${extendDuration}s & pushed to Remotion (${sd.compositionId})! backgroundAudio = "${sd.remotionPath}"`);
                        } catch {}
                      } catch { setAudioGenMessage('❌ Loop failed.'); }
                      finally { setGeneratingAudio(false); }
                    }}
                    disabled={generatingAudio}
                    className="px-4 py-1.5 rounded-lg text-sm font-medium bg-purple-500/10 border border-purple-500/30 text-purple-400 hover:bg-purple-500/20 transition-all disabled:opacity-50"
                  >
                    🔁 Loop to {extendDuration}s
                  </button>

                  <button
                    onClick={() => {
                      setCurrentPreview(null);
                      setAudioGenMessage('');
                      document.querySelector<HTMLButtonElement>('[data-generate-audio]')?.click();
                    }}
                    disabled={generatingAudio}
                    className="px-4 py-1.5 rounded-lg text-sm font-medium bg-orange-500/10 border border-orange-500/30 text-orange-400 hover:bg-orange-500/20 transition-all disabled:opacity-50"
                  >
                    🔄 Regenerate
                  </button>
                </div>
              </div>
            ) : uploadedAudioPath ? (
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">Generated Audio</label>
                <audio controls src={uploadedAudioPath} className="w-full h-10" />
              </div>
            ) : null}

            {/* Voiceover Section */}
            <div className="border-t border-gray-700 pt-4 mt-4 space-y-3">
              <label className="block text-sm text-gray-400 font-medium">🎙️ Voiceover</label>
              <p className="text-xs text-gray-500">Generate a voiceover from your script using ElevenLabs TTS (v3)</p>

              {/* Hidden audio element for voice previews */}
              <audio ref={previewAudioRef} className="hidden" onEnded={() => setPlayingPreview(null)} />

              {voices.length > 0 && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Voice</label>
                  <div className="flex gap-2 items-center min-w-0">
                    <select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      className="bg-white/5 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none min-w-0 flex-1 truncate"
                    >
                      {voices.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.name} {v.labels?.gender ? `· ${v.labels.gender}` : ''} {v.labels?.accent ? `· ${v.labels.accent}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        const voice = voices.find(v => v.id === selectedVoice);
                        if (voice?.preview_url && previewAudioRef.current) {
                          if (playingPreview === voice.id) {
                            previewAudioRef.current.pause();
                            setPlayingPreview(null);
                          } else {
                            previewAudioRef.current.src = voice.preview_url;
                            previewAudioRef.current.play();
                            setPlayingPreview(voice.id);
                          }
                        }
                      }}
                      className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-sm bg-white/5 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-all"
                      title="Preview voice"
                    >
                      {playingPreview === selectedVoice ? '⏹' : '▶'}
                    </button>
                  </div>
                </div>
              )}

              {/* Voiceover script display */}
              {voiceoverScript ? (
                <div className="p-2 rounded-lg bg-white/5 border border-gray-700">
                  <p className="text-xs text-gray-500 mb-1">Voiceover script (edit in Content step):</p>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{voiceoverScript.slice(0, 200)}{voiceoverScript.length > 200 ? '...' : ''}</p>
                </div>
              ) : (
                <p className="text-xs text-orange-400">⚠️ No voiceover script yet — add one in the Content step (Step 2)</p>
              )}

              <button
                onClick={async () => {
                  const scriptText = voiceoverScript || fields.script || fields.caption || fields.hook;
                  if (!scriptText) {
                    alert('Write a voiceover script first (Step 2, Content section).');
                    return;
                  }
                  if (!selectedVoice) {
                    alert('Select a voice first.');
                    return;
                  }
                  setGeneratingVoiceover(true);
                  setVoiceoverMessage('🎙️ Generating voiceover with ElevenLabs TTS v3...');
                  try {
                    const res = await fetch('/api/content/generate-audio', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        type: 'voiceover',
                        text: scriptText,
                        voiceId: selectedVoice,
                        postId: postIdRef.current,
                      }),
                    });
                    const data = await res.json();
                    if (data.error) {
                      setVoiceoverMessage(`❌ ${data.error}`);
                      return;
                    }
                    setVoiceoverOptions(data.paths || []);
                    setVoiceoverMessage('✅ Voiceover generated!');
                  } catch {
                    setVoiceoverMessage('❌ Voiceover generation failed.');
                  } finally {
                    setGeneratingVoiceover(false);
                  }
                }}
                disabled={generatingVoiceover || (!voiceoverScript && !fields.script)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-purple-500/10 border border-purple-500/30 text-purple-400 hover:bg-purple-500/20 hover:shadow-[0_0_8px_rgba(168,85,247,0.3)] transition-all disabled:opacity-50"
              >
                {generatingVoiceover ? <Loader2 className="w-4 h-4 animate-spin" /> : '🎙️'} Generate Voiceover
              </button>

              {voiceoverMessage && (
                <p className="text-sm text-gray-300">{voiceoverMessage}</p>
              )}

              {voiceoverOptions.map((voPath, i) => (
                <div key={voPath} className="flex items-center gap-3">
                  <audio controls src={voPath} className="flex-1 h-10" />
                  <button
                    onClick={async () => {
                      // Push voiceover to Remotion — MUST target correct composition
                      if (!compositionId && !selectedProjectSlug) {
                        setVoiceoverMessage('⚠️ No project selected — cannot push to Remotion safely.');
                        return;
                      }
                      try {
                        const res = await fetch('/api/content/studio-audio', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ audioPath: voPath, compositionId, projectSlug: selectedProjectSlug, audioType: 'voiceover' }),
                        });
                        const data = await res.json();
                        if (data.ok) {
                          setVoiceoverMessage(`✅ Voiceover pushed to Remotion (${data.compositionId})!`);
                        } else {
                          setVoiceoverMessage(`⚠️ ${data.error || 'Push failed'}`);
                        }
                      } catch {
                        setVoiceoverMessage('⚠️ Failed to push voiceover to Remotion.');
                      }
                    }}
                    className="px-3 py-1 rounded text-xs font-medium bg-purple-500/10 border border-purple-500/30 text-purple-400 hover:bg-purple-500/20 transition-all"
                  >
                    Push to Remotion
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between pt-2">
            <GlowButton variant="ghost" size="sm" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4" /> Back
            </GlowButton>
            <GlowButton variant="cyan" size="sm" onClick={() => setStep(3)}>
              Next <ArrowRight className="w-4 h-4" />
            </GlowButton>
          </div>
        </NeonCard>
      )}

      {/* Step 4: Schedule & Submit */}
      {step === 3 && (
        <NeonCard glow="cyan" className="p-6 space-y-5">
          {/* Preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-3">Preview</h3>
            <div className="bg-white/5 border border-gray-700 rounded-lg p-4 space-y-2">
              <div className="flex gap-2 flex-wrap">
                {selectedPlatforms.map(p => {
                  const plat = PLATFORMS.find(x => x.id === p);
                  return (
                    <span key={p} className="text-xs bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded">
                      {plat?.emoji} {plat?.label}
                    </span>
                  );
                })}
              </div>
              {fields.hook && <p className="text-sm font-bold text-white">{fields.hook}</p>}
              {fields.caption && <p className="text-sm text-gray-300 whitespace-pre-wrap">{fields.caption}</p>}
              {fields.script && !fields.caption && (
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{fields.script.slice(0, 500)}</p>
              )}
              {(uploadedVideoPath || uploadedImagePath) && (
                <p className="text-xs text-gray-500">📎 {uploadedVideoPath || uploadedImagePath}</p>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Schedule</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="bg-white/5 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none"
              />
              {scheduledAt && (
                <button onClick={() => setScheduledAt('')} className="text-gray-500 hover:text-red-400">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <GlowButton variant="ghost" size="sm" onClick={() => setStep(2)}>
              <ArrowLeft className="w-4 h-4" /> Back
            </GlowButton>
            <GlowButton variant="ghost" size="sm" onClick={() => handleSubmit('draft')} disabled={saving}>
              Save Draft
            </GlowButton>
            {scheduledAt && (
              <GlowButton variant="cyan" size="sm" onClick={() => handleSubmit('scheduled')} disabled={saving}>
                <Calendar className="w-4 h-4" />
                Schedule
              </GlowButton>
            )}
            <GlowButton variant="cyan" size="sm" onClick={() => handleSubmit('pending_review')} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit for Review
            </GlowButton>
          </div>
        </NeonCard>
      )}

      {/* Auto-save indicator */}
      {postId && (
        <p className="text-xs text-gray-600 text-right">Auto-saved as draft • ID: {postId.slice(0, 8)}...</p>
      )}
    </div>
  );
}
